import { z } from "zod";
import {
  allowedCategories,
  budgetRefreshIntervals,
  budgetReportFrequencies,
  emailPreferenceFrequencies,
  type MemberRole,
  memberRoles,
  monthlySpendOptions,
  organizationSizeOptions,
  refreshIntervals,
  stateCodes,
} from "./constants";
import { validateRoutingNumber } from "./sharedUtils";

export const phoneValidator = z
  .string()
  .regex(/^\+1[2-9]\d{9}$/, "Must be a valid US phone number");

//* --- Auth ---
export const signUpServer = z.object({
  email: z
    .string()
    .email()
    .transform((email) => email.toLowerCase()),
  enrollEmail: z.boolean(),
  first: z
    .string()
    .min(1, "Required")
    .superRefine((val, ctx) => {
      if (val.length > 0 && !/^[\p{L}\s'-]+$/u.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "First name must contain only letters, spaces, apostrophes, and hyphens",
        });
      }
    }),
  last: z
    .string()
    .min(1, "Required")
    .superRefine((val, ctx) => {
      if (val.length > 0 && !/^[\p{L}\s'-]+$/u.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Last name must contain only letters, spaces, apostrophes, and hyphens",
        });
      }
    }),
  password: z.string().min(8, "Password must be at least 8 characters"),
  phone: phoneValidator,
});

export const signUpServerInvite = signUpServer.extend({
  inviteId: z.string(),
});

export const signUpClientInvite = signUpServerInvite
  .extend({
    confirm: z.string().min(8),
  })
  .refine((data) => data.password === data.confirm, {
    message: "Passwords don't match",
    path: ["confirm"],
  });

export const changeName = signUpServer.pick({
  first: true,
  last: true,
});

export const signUpOrganization = signUpServer.extend({
  dbaName: z.string(),
  ein: z.string().length(9, "EIN must be 9 digits long"),
  monthlySpend: z.enum(
    monthlySpendOptions.map((o) => o.value) as [string, ...string[]],
  ),
  organizationName: z.string().min(1, "Required"),
  organizationSize: z.enum(
    organizationSizeOptions.map((o) => o.value) as [string, ...string[]],
  ),
  referralCode: z.string().optional(),
  utm: z.string().nullable(),
});

export const signUpStepValidators = [
  signUpOrganization
    .pick({
      dbaName: true,
      email: true,
      first: true,
      last: true,
      password: true,
      phone: true,
    })
    .extend({
      confirm: z.string().min(8),
    })
    .refine((data) => data.password === data.confirm, {
      message: "Passwords don't match",
      path: ["confirm"],
    }),
  signUpOrganization.pick({
    ein: true,
    monthlySpend: true,
    organizationName: true,
    organizationSize: true,
  }),
];

// Address schema validator based on the specified requirements
export const addressSchema = z.object({
  // Address first line, e.g. street number and street name
  addressLine1: z.string().min(1, "Address line 1 is required"),

  // Optional address second line, e.g. apartment number, suite, etc.
  addressLine2: z.string().optional().nullable(),

  // Name of the city
  city: z.string().min(1, "City is required"),

  // Two letter US state abbreviation or full state name
  state: z.enum(stateCodes, {
    message: "Invalid state code",
  }),

  // 5 digit US zip code
  zipcode: z.string().length(5),
});

// Doesn't enforce things like 5 digit zip code
export const optionalAddressSchema = z.object({
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string(),
  state: z.enum(stateCodes),
  zipcode: z.string(),
});

//* --- Organization ---
export const createOrg = z.object({
  address: addressSchema,
  dbaName: z.string().nullable(),
  ein: z.string().length(9, "EIN must be 9 digits long"),
  name: z.string().min(1, "Required"),
});

export const editOrg = z.object({
  dbaName: z.string().nullable(),
  defaultAddressId: z.string(),
  enableTransparency: z.boolean(),
});

export const updateBillingAddressValidator = z.object({
  address: addressSchema,
});

export const updateShippingAddressValidator = z.object({
  addressId: z.string(),
});

export const createAddressValidator = z.object({
  address: addressSchema.extend({
    name: z.string().nullable(),
  }),
});

export const updateAddressValidator = z.object({
  address: addressSchema.extend({
    name: z.string().nullable(),
  }),
  addressId: z.string(),
});

export const deleteAddressValidator = z.object({
  addressId: z.string(),
});

export const invitesValidator = z.object({
  invites: z
    .object({
      email: z
        .string()
        .email()
        .transform((email) => email.toLowerCase()),
      role: z.enum(["member", "viewOnlyAdmin", "bookkeeper", "admin"]),
    })
    .array(),
});

//* --- Budget and Restrictions ---
export const budgetReportingScheduleValidator = z.object({
  frequency: z.enum(budgetReportFrequencies),
  groupId: z.string(),
  users: z.string().array(),
});

export const categoriesValidator = z.enum(allowedCategories).array();
const scheduleValidator = z
  .object({
    activateAt: z.date().nullable(),
    lockAt: z.date().nullable(),
  })
  .refine((data) => {
    if (data.activateAt && data.lockAt && data.activateAt > data.lockAt) {
      return false;
    }
    return true;
  });

export const restrictionsValidator = z.object({
  allowedCategories: categoriesValidator,
  periodLimitCents: z.number().int().nonnegative(),
  periodTargetCents: z.number().int().min(1, "Spend limit cannot be zero"),
  refreshInterval: z.enum(refreshIntervals),
  schedule: scheduleValidator.nullable().optional(),
});

export const editableRestrictionsValidator = restrictionsValidator.pick({
  allowedCategories: true,
  periodLimitCents: true,
  periodTargetCents: true,
  refreshInterval: true,
  schedule: true,
});

export const restrictionsBase = z.object({
  categories: categoriesValidator,
  limitCents: z.number().int().nonnegative().nullable(),
});

export const restrictionsWithOptionalInterval = restrictionsBase
  .extend({
    interval: z
      .enum(refreshIntervals)
      .nullable()
      .or(z.literal("null").transform(() => null)),
  })
  .refine((data) => {
    return (
      (data.limitCents !== null && data.interval !== null) ||
      (data.limitCents === null && data.interval === null)
    );
  });

export type BudgetDurationType = z.infer<typeof budgetTimingOptions>;

const budgetTimingOptions = z
  .discriminatedUnion("type", [
    z.object({
      refreshInterval: z.enum(budgetRefreshIntervals as [string, ...string[]]),
      type: z.literal("repeating"),
    }),
    z.object({
      end: z.date().nullable().optional(),
      start: z.date().nullable().optional(),
      type: z.literal("once"),
    }),
  ])
  .refine(
    (data) => {
      if (data.type === "once") {
        // Either both are provided or neither
        const isStartDateProvided = data.start != null;
        const isEndDateProvided = data.end != null;

        return isStartDateProvided === isEndDateProvided;
      }

      return true;
    },
    {
      message: "Required",
      path: ["start"],
    },
  );

export const editCardValidator = z.object({
  approvalPolicyId: z.string().nullable(),
  defaultBudgetId: z.string().nullable(),
  description: z.string().nullable(),
  name: z.string().min(1, "Name is required"),
  policyId: z.string().nullable(),
  restrictions: editableRestrictionsValidator.nullable(),
});

export const createBudgetBase = z.object({
  description: z.string().nullable(),
  endDate: z.date().optional().nullable(),
  name: z.string().min(1, "Name is required"),
  startDate: z.date().optional(),
  subBudgets: z
    .array(
      z.object({
        name: z.string().min(1, "Required"),
        targetCents: z
          .number()
          .int()
          .nonnegative()
          .min(100, "Amount must be at least $1"),
      }),
    )
    .min(1),
  timing: budgetTimingOptions,
  users: z.string().array(),
});

export const editBudgetGroup = z.object({
  endDate: z.date().nullable(),
  groupId: z.string(),
  name: z.string().nonempty(),
  startDate: z.date().nullable(),
  subBudgets: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().min(1, "Required"),
      targetCents: z.number().int().nonnegative(),
    }),
  ),
  timing: budgetTimingOptions,
  users: z.string().array(),
});

// export const editBudgetClient = editBudgetBase.extend({
//   tags: z.string().array(),
// });

// export const editBudgetServer = editBudgetBase.extend({
//   addTagIds: z.string().array(),
//   removeTagIds: z.string().array(),
// });

export const budgetDetails = z.object({
  description: z.string(),
  name: z.string(),
});

export const editSubBudget = z.object({
  budgetId: z.string(),
  description: z.string().optional().nullable(),
  name: z.string().min(1, "Name is required"),
  targetCents: z.number().int().nonnegative(),
});

//* --- Cards ---

export const createIndividualCardBase = z.object({
  address: addressSchema.optional(),
  approvalPolicyId: z.string().nullable(),
  defaultBudgetId: z.string().nullable(),
  description: z.string().nullable(),
  forUserId: z.string({ invalid_type_error: "Required" }),
  name: z.string().min(1, "Required"),
  policyId: z.string().nullable(),
  requestId: z.string().optional(),
  restrictions: restrictionsValidator,
  saveNewAddress: z.boolean(),
  type: z.enum(["physical", "virtual"], {
    message: "Required",
  }),
});

export const createIndividualCardClient = createIndividualCardBase
  .omit({
    address: true,
    requestId: true,
  })
  .extend({
    address: addressSchema.optional(),
    addressId: z.string().min(1).nullable(), // Conditionally applying this on the client is difficult, so the validation can just happen first thing server side
  })
  .refine(
    (data) => {
      if (data.type === "physical") {
        // TODO make sure all address values are defined for physical cards
        return data.address !== undefined && data.addressId !== null;
      }
      return true;
    },
    {
      message: "Shipping Address is Required",
      path: ["addressId"],
    },
  );

export const createIndividualCardServer = createIndividualCardBase.refine(
  (data) => {
    if (data.type === "physical") {
      return data.address !== undefined;
    }
    return true;
  },
);

//* --- Referrals ---
export const referralEmailsValidator = z.object({
  invites: z
    .object({
      email: z
        .string()
        .email()
        .transform((email) => email.toLowerCase()),
    })
    .array(),
});

//* --- Policies ---

export const expensePolicyBase = z.object({
  description: z.string().nullable(),
  excludeVirtualReceipt: z.boolean(),
  memoCents: z.number().int().nonnegative().nullable(),
  memoPrompt: z.string().nullable(),
  name: z.string().min(1, "Name is required"),
  receiptCents: z.number().int().nonnegative().nullable(),
  requireBudget: z.boolean(),
});

export const createExpensePolicyClient = expensePolicyBase.extend({
  requireMemo: z.boolean(),
  requireReceipt: z.boolean(),
});

export const excludedApprovalRoles: MemberRole[] = [
  "none",
  "member",
  "viewOnlyAdmin",
];
export const approvalObjectValidator = z.union([
  z.object({
    role: z.enum(
      memberRoles.filter((r) => !excludedApprovalRoles.includes(r)) as [
        MemberRole,
        ...MemberRole[],
      ],
    ),
    type: z.literal("role"),
  }),
  z.object({
    type: z.literal("member"),
    userId: z.string(),
  }),
]);

export const createApprovalPolicy = z.object({
  approvers: z.array(approvalObjectValidator).min(1, "Required"),
  description: z.string().nullable(),
  groupOperator: z.enum(["all", "any"]),
  name: z.string().min(1, "Name is required"),
});

export type ApprovalObject = z.infer<typeof approvalObjectValidator>;

//* --- Accounting ---

// Using a refine instead of a union makes the type handling easier on the client
export const selectLiabilityAccount = z
  .object({
    name: z.string(),
    paymentAccountId: z.string(),
    type: z.enum(["new", "existing"]),
  })
  .refine(
    (data) => {
      if (data.type === "new") {
        return data.name.length > 0;
      }
      return true;
    },
    {
      message: "Enter a name for the new account",
    },
  )
  .refine(
    (data) => {
      if (data.type === "existing") {
        return data.paymentAccountId.length > 0;
      }
      return true;
    },
    {
      message: "Select an existing account",
    },
  );

const upsertRule = z
  .object({
    accountId: z.string().nullable(),
    forItem: z.string(),
    liabilityAccountId: z.string().nullable(),
    ruleId: z.string().nullable(),
    type: z.enum(["card", "budget", "category"]),
  })
  .refine((data) => {
    if (data.type === "category") {
      // @ts-ignore
      return allowedCategories.includes(data.forItem);
    }
    return true;
  });

export const upsertRulesBatch = z.object({
  rules: z.array(upsertRule),
});

export type AccountingSplit = z.infer<typeof accountingSplit>;

const accountingSplit = z.object({
  amountCents: z.number().int(),
  description: z.string(),
  expenseAccountId: z.string(),
  expenseAccountPlatformId: z.string(),
});

// * --- Card Requests ---

export const requestClientValidator = z.object({
  files: z.array(
    z.object({
      contentType: z.string(),
      id: z.string(),
      name: z.string(),
      size: z.number(),
      url: z.string(),
    }),
  ),
  reason: z.string().min(1, "Required"),
});

export const cardRequestValidator = createIndividualCardClient
  .innerType()
  .pick({
    addressId: true,
    forUserId: true,
    name: true,
    saveNewAddress: true,
    type: true,
  })
  .and(
    z.object({
      address: addressSchema.optional(),
      restrictions: restrictionsValidator.pick({
        periodTargetCents: true,
        refreshInterval: true,
      }),
    }),
  );

export type CardRequestData = z.infer<typeof cardRequestValidator>;

export const cardLimitIncreaseRequestValidator = z.object({
  cardId: z.string(),
  newTargetCents: z.number().int().min(1, "New limit must be at least $0.01"),
});

export type CardLimitIncreaseRequest = z.infer<
  typeof cardLimitIncreaseRequestValidator
>;

// * --- Reimbursement ---

export const reimbursementRequestValidator = z.object({
  amountCents: z.number().int().min(1, "Amount must be at least $0.01"),
  bankAccountId: z.string().optional().nullable(),
  date: z
    .date({
      invalid_type_error: "Date is required",
      required_error: "Date is required",
    })
    .refine((date) => {
      return date <= new Date();
    }),
  memo: z
    .string({ invalid_type_error: "Memo is required" })
    .nonempty("Memo is required"),
  merchant: z.string().nonempty("Merchant name is required"),
  reimbursementId: z.string(),
});

export const bankAccountValidator = z.object({
  accountNumber: z.string().regex(/^\d+$/, "Invalid account number"),
  name: z.string().min(1, "Required"),
  routingNumber: z
    .string()
    .min(9, "Invalid routing number")
    .refine((val) =>
      validateRoutingNumber(val) ? true : "Invalid routing number",
    ),
});

export type ReimbursementRequest = Partial<
  z.infer<typeof reimbursementRequestValidator>
>;

// * --- Requests ---

export const requestValidator = z.object({
  // Used to update `forItemId` in files table after request creation
  fileIds: z.array(z.string()).optional(),

  // Common properties to all requests
  reason: z.string(),
  request: z.discriminatedUnion("type", [
    z.object({
      data: cardRequestValidator,
      type: z.literal("card"),
    }),
    //* Later add more options
    z.object({
      data: reimbursementRequestValidator,
      type: z.literal("request"),
    }),
    z.object({
      data: cardLimitIncreaseRequestValidator,
      type: z.literal("limit"),
    }),
  ]),
});

export type RequestCreationType = z.infer<typeof requestValidator>;

// * --- User Preferences ---

export const preferencesValidator = z.object({
  approvalNeededReminders: z
    .enum(emailPreferenceFrequencies)
    .nullable()
    .optional(),
  creditLimitReminders: z.boolean().nullable().optional(),
  incompleteTransactionReminders: z
    .enum(emailPreferenceFrequencies)
    .nullable()
    .optional(),
  reimbursementBankAccount: z.string().nullable().optional(),
});
